import { CONSENT, isConsentGranted } from "./consent"
import Cookies from "./cookies"
import { sessionStorage } from "./storage"

declare global {
  interface Window {
    // https://documentation.bloomreach.com/data-hub/docs/sdk-web-tracking
    brweb?: {
      identify(customerIds: object, customerProperties?: object): void
      track(
        eventName: string,
        eventProperties?: EventProperties,
        // SDK uses `fetch keepalive` on `pagehide` but we're told by Bloomreach not to rely on
        // that, hence callbacks to track events preceding a redirect.
        successCallback?: () => void,
        errorCallback?: () => void
      ): void
    }
  }
}

type EventProperties = Record<string, unknown>

const ABANDONED_CARTS_PATH = "/bloomreach/abandoned_carts"
const EMAIL_COOKIE = "email_address"
const NAVIGATION_TIMEOUT = 500
const SEARCH_TERM_STORAGE_KEY = "bloomreach_search_term"

const abandonedCartBeacon = (
  cartData: EventProperties,
  productIds: Array<string>,
  totalPrice: number
): void => {
  if (!isConsentGranted(CONSENT.TARGETING)) {
    return
  }

  const properties = {
    ...cartData,
    item_count: productIds.length,
    product_ids: productIds,
    product_list: productIds.map((productId) => ({ product_id: productId, quantity: 1 })),
    total_price: totalPrice
  }

  window.navigator.sendBeacon(
    ABANDONED_CARTS_PATH,
    new Blob([JSON.stringify({ properties })], { type: "application/json" })
  )
}

const identify = (): void => {
  if (!window.brweb || !isConsentGranted(CONSENT.TARGETING)) {
    return
  }

  const email = Cookies.get(EMAIL_COOKIE)?.trim().toLowerCase()

  if (!email) {
    return
  }

  window.brweb.identify({ email_id: email })
}

const saveSearchTermToStorage = (searchTerm: string): void => {
  sessionStorage?.setItemIfConsent(CONSENT.TARGETING, SEARCH_TERM_STORAGE_KEY, searchTerm)
}

const searchEvent = (eventProperties: EventProperties): void => {
  const searchTerm = sessionStorage?.getItem(SEARCH_TERM_STORAGE_KEY)

  // Presence of `searchTerm` denotes that user search was performed. Its absence (null or undefined)
  // indicates page loaded via non-search means (e.g. category page link from newsletter, venue
  // page click from home page, etc.) An empty string is a real search with no typed input so
  // it's still valid.
  if (typeof searchTerm !== "string") {
    return
  }

  sessionStorage?.removeItem(SEARCH_TERM_STORAGE_KEY)
  track("search", { ...eventProperties, search_term: searchTerm })
}

const track = (
  eventName: string,
  eventProperties?: EventProperties,
  successCallback?: () => void,
  errorCallback?: () => void
): boolean => {
  if (!window.brweb || !isConsentGranted(CONSENT.TARGETING)) {
    return false
  } else {
    window.brweb.track(eventName, eventProperties, successCallback, errorCallback)
    return true // Reflects whether tracking is available, not whether it succeeded or failed.
  }
}

const trackOnLinkClick = (
  eventName: string,
  eventProperties: EventProperties,
  event: MouseEvent
): void => {
  const { href } = event.currentTarget as HTMLAnchorElement
  const navigate = (): void => window.location.assign(href)

  // If tracking is available, prevent link click so tracking can proceed before unload.
  // Navigation will occur on either success or failure, but also set a timeout in case neither
  // callback fires.
  if (track(eventName, eventProperties, navigate, navigate)) {
    event.preventDefault()
    window.setTimeout(navigate, NAVIGATION_TIMEOUT)
  }
}

const viewItemEvent = (eventProperties: EventProperties): void => {
  track("view_item", eventProperties)
}

const viewItemEventOnLinkClick = (eventProperties: EventProperties, event: MouseEvent): void => {
  trackOnLinkClick("view_item", eventProperties, event)
}

export {
  type EventProperties,
  abandonedCartBeacon,
  identify,
  saveSearchTermToStorage,
  searchEvent,
  viewItemEvent,
  viewItemEventOnLinkClick
}
